import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  TextField,
  Button,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Link,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Autocomplete
} from '@mui/material';
import { useUserSession } from '../../contexts/UserContext';
import authApi from '../../services/authApi';
import { sharePointService } from '../../services/sharePointService';
import { pickPrimarySharePointConfig, normalizeSharePointConfigRow } from '../../utils/sharePointConfigRowUtils';
import NavigationDrawer from '../Navigation/NavigationDrawer';
import { useNavigate } from 'react-router-dom';

function SharePointConfig({ configOwnerEmail: configOwnerEmailProp = '', configOwnerDisplayName = '' }) {
  const { userSession } = useUserSession();
  const configOwnerEmail = (configOwnerEmailProp || '').trim();
  const ownerScope = configOwnerEmail || null;
  const isAdminOnBehalf = Boolean(ownerScope);
  const [formData, setFormData] = useState({
    tenantUrl: '',
    clientId: '',
    clientSecret: '',
    tenantId: '',
  });
  const [testStatus, setTestStatus] = useState(null);
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const [subSites, setSubSites] = useState([]);
  const [newSitePath, setNewSitePath] = useState('');
  const [noSavedSpConfig, setNoSavedSpConfig] = useState(false);
  const [discoverOptions, setDiscoverOptions] = useState([]);
  const [isDiscoveringSites, setIsDiscoveringSites] = useState(false);
  const [isTestingSitePath, setIsTestingSitePath] = useState(false);
  const [sitePathTestStatus, setSitePathTestStatus] = useState(null);
  const [siteActionStatus, setSiteActionStatus] = useState(null);
  const [deletingSiteId, setDeletingSiteId] = useState(null);
  const [selectedDiscoverSite, setSelectedDiscoverSite] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const loadConfig = async () => {
      try {
        setNoSavedSpConfig(false);
        if (!userSession?.email) {
          console.log('Waiting for user session...');
          return;
        }

        console.log('Loading SharePoint config for user:', ownerScope || userSession.email);
        const token = authApi.getToken();
        console.log('Auth token obtained, length:', token.length);
        
        const config = await sharePointService.getConfig(token, ownerScope);
        console.log('SharePoint config loaded:', config);
        
        if (!isMounted) return;

        if (config && config.success === false) {
          console.log('Config not found or error:', config.message);
          setError(config.message || 'Configuration not found');
        } else if (config) {
          let configData = null;
          if (config.configs && config.configs.length > 0) {
            configData = pickPrimarySharePointConfig(config.configs);
          } else if (config.id || config.tenant_url || config.Tenant_url) {
            configData = normalizeSharePointConfigRow(config);
          }

          console.log('Setting form data from config:', configData);
          if (
            ownerScope &&
            config.success !== false &&
            (!config.configs || config.configs.length === 0)
          ) {
            setNoSavedSpConfig(true);
          }
          if (configData) {
            const masked =
              configData.client_secret === '********'
                ? '********'
                : configData.client_secret || '';
            setFormData({
              id: configData.id,
              tenantUrl: configData.tenant_url || '',
              clientId: configData.client_id || '',
              clientSecret: masked,
              tenantId: configData.tenant_id || '',
            });
          }
        } else {
          console.log('No configuration found, using empty form');
        }
      } catch (error) {
        console.error('Error loading configuration:', error);
        if (isMounted) {
          setError(error.message || 'Failed to load configuration');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadConfig();

    return () => {
      isMounted = false;
    };
  }, [userSession, ownerScope]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let updatedValue = value.trim();
    
    // Special handling for client secret
    if (name === 'clientSecret') {
      if (value !== '********') {
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
      }
      return;
    }
    
    // Special handling for tenant URL
    if (name === 'tenantUrl') {
      try {
        // Parse the URL to extract just the hostname
        const url = new URL(updatedValue);
        if (url.hostname.includes('.sharepoint.com')) {
          // Only keep the protocol and hostname
          updatedValue = `${url.protocol}//${url.hostname}`;
        }
      } catch (error) {
        // If URL parsing fails, try basic string manipulation
        if (updatedValue.includes('.sharepoint.com')) {
          // Extract everything up to .sharepoint.com
          const match = updatedValue.match(/(https?:\/\/)?([^\/]+\.sharepoint\.com)/i);
          if (match) {
            updatedValue = `https://${match[2]}`;
          }
        }
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: updatedValue
    }));
  };

  const handleTestConnection = async () => {
    setIsTestingConnection(true);
    setTestStatus(null);
    
    try {
      const token = authApi.getToken();
      
      // Clean up the tenant URL
      let cleanTenantUrl = formData.tenantUrl.trim();
      
      // Remove any newlines or carriage returns that might be causing issues
      cleanTenantUrl = cleanTenantUrl.replace(/[\r\n\t]/g, '');
      
      if (!cleanTenantUrl) {
        throw new Error('Tenant URL is required');
      }
      
      // Allow both SharePoint and Microsoft 365 URLs
      try {
        // Parse the URL to make sure it's valid
        const url = new URL(cleanTenantUrl);
        // Only keep the protocol and hostname
        cleanTenantUrl = `${url.protocol}//${url.hostname}`;
        console.log('Parsed tenant URL:', cleanTenantUrl);
      } catch (error) {
        // If URL parsing fails, ensure it has the right protocol
        if (!cleanTenantUrl.startsWith('https://')) {
          cleanTenantUrl = `https://${cleanTenantUrl}`;
        }
        console.log('URL parsing failed, defaulting to:', cleanTenantUrl);
      }

      // More flexible URL validation - accept common SharePoint domains
      const validDomains = [
        '.sharepoint.com',
        '.microsoft.com',
        '.office.com',
        '.onmicrosoft.com'
      ];
      
      const isValidDomain = validDomains.some(domain => 
        cleanTenantUrl.toLowerCase().includes(domain)
      );
      
      // Less strict validation - allow for testing even if domain not recognized
      if (!isValidDomain) {
        console.warn('Tenant URL might not be a standard SharePoint domain:', cleanTenantUrl);
      }

      console.log('Testing connection with URL:', cleanTenantUrl);
      
      // Validate client ID and tenant ID before sending
      if (!formData.clientId.trim()) {
        throw new Error('Azure AD Application (Client) ID is required');
      }
      
      if (!formData.tenantId.trim()) {
        throw new Error('Tenant ID is required');
      }
      
      const testData = {
        tenant_url: cleanTenantUrl,
        client_id: formData.clientId.trim(),
        client_secret: formData.clientSecret === '********' ? null : formData.clientSecret.trim(),
        tenant_id: formData.tenantId.trim()
      };

      console.log('Sending test connection data:', {
        ...testData,
        client_secret: testData.client_secret ? '********' : null
      });
      
      const result = await sharePointService.testConnection(token, testData, ownerScope);
      console.log('Test connection response:', result);
      
      setTestStatus({
        success: true,
        message: result.message || 'Connection successful!'
      });
    } catch (error) {
      console.error('Test connection error:', error);
      
      // Extract the most useful error message
      let errorMessage = 'Connection failed';
      
      if (error.response?.data?.details?.message) {
        // Detailed error from our backend
        errorMessage = error.response.data.details.message;
      } else if (error.response?.data?.message) {
        // Basic error message from our backend
        errorMessage = error.response.data.message;
      } else if (error.message) {
        // Error from axios or other source
        errorMessage = error.message;
      }
      
      setTestStatus({
        success: false,
        message: `${errorMessage} (Attempted URL: ${formData.tenantUrl})`
      });
    } finally {
      setIsTestingConnection(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const token = authApi.getToken();
      
      // Clean up the tenant URL
      let cleanTenantUrl = formData.tenantUrl.trim();
      
      // Remove any newlines or carriage returns that might be causing issues
      cleanTenantUrl = cleanTenantUrl.replace(/[\r\n\t]/g, '');
      
      if (!cleanTenantUrl) {
        throw new Error('Tenant URL is required');
      }
      
      try {
        const url = new URL(cleanTenantUrl);
        cleanTenantUrl = `${url.protocol}//${url.hostname}`;
        console.log('Parsed tenant URL for saving:', cleanTenantUrl);
      } catch (error) {
        if (!cleanTenantUrl.startsWith('https://')) {
          cleanTenantUrl = `https://${cleanTenantUrl}`;
        }
        console.log('URL parsing failed for saving, defaulting to:', cleanTenantUrl);
      }

      const saveData = {
        tenant_url: cleanTenantUrl,
        client_id: formData.clientId.trim(),
        client_secret: formData.clientSecret === '********' ? null : formData.clientSecret.trim(),
        tenant_id: formData.tenantId.trim()
      };
      
      // Validate required fields before sending
      if (!saveData.tenant_url || !saveData.client_id || !saveData.tenant_id) {
        throw new Error('All fields are required');
      }
      
      // More flexible URL validation - accept common SharePoint domains
      const validDomains = [
        '.sharepoint.com',
        '.microsoft.com',
        '.office.com',
        '.onmicrosoft.com'
      ];
      
      const isValidDomain = validDomains.some(domain => 
        cleanTenantUrl.toLowerCase().includes(domain)
      );
      
      // Less strict validation - warn but don't block saving
      if (!isValidDomain) {
        console.warn('Tenant URL might not be a standard SharePoint domain:', cleanTenantUrl);
      }

      await sharePointService.saveConfig(token, saveData, ownerScope);

      try {
        const refreshed = await sharePointService.getConfig(token, ownerScope);
        if (refreshed && refreshed.success !== false && refreshed.configs && refreshed.configs.length > 0) {
          const primary = pickPrimarySharePointConfig(refreshed.configs);
          const row = primary || refreshed.configs[0];
          const newId = row.id ?? row.Id;
          if (newId != null) {
            setFormData((prev) => ({
              ...prev,
              id: newId,
              tenantUrl: row.tenant_url || prev.tenantUrl,
              clientId: row.client_id || prev.clientId,
              tenantId: row.tenant_id || prev.tenantId
            }));
          }
        }
      } catch (reErr) {
        console.warn('Could not refresh configuration id after save', reErr);
      }
      fetchSites();
      setNoSavedSpConfig(false);

      setTestStatus({
        success: true,
        message: 'Configuration saved successfully'
      });
    } catch (error) {
      console.error('Save configuration error:', error);
      setTestStatus({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to save configuration'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const isSubsiteRow = (s) => s.is_subsite === true || s.is_subsite === 1;

  // fetch sites (parent + subsites)
  const fetchSites = async () => {
    try {
      const token = authApi.getToken();
      const res = await sharePointService.listSites(token, ownerScope);
      if (res.success) {
        setSubSites(res.data.filter(isSubsiteRow));
      }
    } catch (e) {
      console.error('fetchSites', e);
    }
  };

  useEffect(() => {
    if (userSession?.email) {
      fetchSites();
    }
  }, [userSession, ownerScope]);

  const handleDiscoverSites = async () => {
    if (!formData.id) {
      setSiteActionStatus({ success: false, message: 'Save the main configuration before browsing sites.' });
      return;
    }
    setIsDiscoveringSites(true);
    setSiteActionStatus(null);
    try {
      const token = authApi.getToken();
      const res = await sharePointService.discoverSharePointSites(token, formData.id, '*', ownerScope);
      if (res.success) {
        setDiscoverOptions(res.sites || []);
        if (!res.sites?.length) {
          setSiteActionStatus({
            success: false,
            message: 'No additional SharePoint sites were found. You can still enter a path manually.'
          });
        }
      } else {
        setSiteActionStatus({ success: false, message: res.message || 'Failed to load sites' });
      }
    } catch (e) {
      setSiteActionStatus({
        success: false,
        message: e.response?.data?.message || e.message || 'Failed to load SharePoint sites'
      });
    } finally {
      setIsDiscoveringSites(false);
    }
  };

  const handleTestSitePath = async () => {
    const path = newSitePath.trim();
    if (!path) {
      setSitePathTestStatus({ success: false, message: 'Enter or select a site path first.' });
      return;
    }
    if (!formData.id) {
      setSitePathTestStatus({ success: false, message: 'Save the main configuration before testing a site path.' });
      return;
    }
    setIsTestingSitePath(true);
    setSitePathTestStatus(null);
    try {
      const token = authApi.getToken();
      const result = await sharePointService.testSitePath(
        token,
        { parentId: formData.id, sitePath: path },
        ownerScope
      );
      setSitePathTestStatus({
        success: true,
        message: result.message || `Connected to ${result.siteUrl || path}`
      });
    } catch (e) {
      setSitePathTestStatus({
        success: false,
        message: e.response?.data?.message || e.message || 'Site path validation failed'
      });
    } finally {
      setIsTestingSitePath(false);
    }
  };

  const handleAddSubSite = async () => {
    const path = newSitePath.trim();
    if (!path) return;
    if (!formData.id) {
      setSiteActionStatus({ success: false, message: 'Save the main configuration before adding sites.' });
      return;
    }
    setSiteActionStatus(null);
    try {
      const token = authApi.getToken();
      await sharePointService.addSubSite(token, { parentId: formData.id, sitePath: path }, ownerScope);
      setNewSitePath('');
      setSelectedDiscoverSite(null);
      setSitePathTestStatus(null);
      await fetchSites();
      setSiteActionStatus({ success: true, message: `Added site ${path}` });
    } catch (e) {
      setSiteActionStatus({
        success: false,
        message: e.response?.data?.message || e.message || 'Failed to add site'
      });
    }
  };

  const handleDeleteSubSite = async (id) => {
    const confirmed = window.confirm(
      'Delete this sub-site and purge all imported warehouse data for it from SecurePoint IQ? This CANNOT be undone.\n\n' +
      'The site will disappear from this list immediately. Purging warehouse data continues in the background and may take several minutes for large libraries.'
    );
    if (!confirmed) return;
    setSiteActionStatus(null);
    setDeletingSiteId(id);
    setSubSites((prev) => prev.filter((s) => (s.id ?? s.Id) !== id));
    try {
      const token = authApi.getToken();
      const res = await sharePointService.deleteSubSite(token, id, true, ownerScope);
      await fetchSites();
      setSiteActionStatus({
        success: true,
        message: res.message || 'Site removed. Warehouse data is being purged in the background.'
      });
    } catch (e) {
      await fetchSites();
      setSiteActionStatus({
        success: false,
        message: e.response?.data?.message || e.message || 'Failed to delete site'
      });
    } finally {
      setDeletingSiteId(null);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', paddingTop: '64px' }}>
        <NavigationDrawer
          isMobile={isMobile}
          isDrawerOpen={isDrawerOpen}
          setIsDrawerOpen={setIsDrawerOpen}
          activeSection={''}
          setActiveSection={() => {}}
          navigate={navigate}
        />
        <Box component="main" sx={{ flexGrow: 1, p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>
            Loading configuration...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', paddingTop: '64px' }}>
      <NavigationDrawer
        isMobile={isMobile}
        isDrawerOpen={isDrawerOpen}
        setIsDrawerOpen={setIsDrawerOpen}
        activeSection={''}
        setActiveSection={() => {}}
        navigate={navigate}
      />
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Typography variant="h4" gutterBottom>
          {isAdminOnBehalf
            ? `SharePoint configuration — ${configOwnerDisplayName || configOwnerEmail}`
            : 'My SharePoint Configuration'}
        </Typography>
        {isAdminOnBehalf && (
          <Alert severity="info" sx={{ mb: 2 }}>
            You are editing SharePoint settings on behalf of{' '}
            <strong>{configOwnerDisplayName ? `${configOwnerDisplayName} (${configOwnerEmail})` : configOwnerEmail}</strong>.
            Changes apply to that user&apos;s account, not yours.
          </Alert>
        )}
        {isAdminOnBehalf && noSavedSpConfig && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This user does not have a saved SharePoint configuration yet. Fill in the fields below and click{' '}
            <strong>Save Configuration</strong> to create the tenant record for their account.
          </Alert>
        )}

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom color="primary">
            Prerequisites
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            To use this feature, you need to have Global Administrator or SharePoint Administrator privileges
            in your Microsoft 365 tenant.
          </Alert>
          
          <Typography variant="body1" paragraph>
            You'll need to set up an Azure AD application with the following permissions:
            <ul>
              <li>Microsoft Graph API permissions:
                <ul>
                  <li>Sites.FullControl.All</li>
                  <li>User.Read.All</li>
                  <li>Group.Read.All</li>
                </ul>
              </li>
              <li>SharePoint API permissions:
                <ul>
                  <li>Sites.FullControl.All</li>
                </ul>
              </li>
            </ul>
          </Typography>
          
          <Alert severity="warning" sx={{ mt: 2 }}>
            Important: After adding these permissions, a user with Global Administrator, Cloud Application Administrator, 
            or Application Administrator role must click "Grant admin consent" in the Azure Portal for your application. 
            Without admin consent, the connection will fail with permission errors.
          </Alert>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              margin="normal"
              label="SharePoint Tenant URL"
              name="tenantUrl"
              value={formData.tenantUrl}
              onChange={handleChange}
              helperText="Example: https://contoso.sharepoint.com"
              required
            />

            <TextField
              fullWidth
              margin="normal"
              label="Azure AD Application (Client) ID"
              name="clientId"
              value={formData.clientId}
              onChange={handleChange}
              required
            />

            <TextField
              fullWidth
              margin="normal"
              label="Client Secret"
              name="clientSecret"
              type="password"
              value={formData.clientSecret}
              onChange={handleChange}
              required
            />

            <TextField
              fullWidth
              margin="normal"
              label="Tenant ID"
              name="tenantId"
              value={formData.tenantId}
              onChange={handleChange}
              required
            />

            {formData.id != null && formData.id !== '' && (
              <Box
                sx={{
                  mt: 2,
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: 'action.hover',
                  border: 1,
                  borderColor: 'divider'
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  Main site{' '}
                  <Typography component="span" variant="body2" fontWeight={600} color="text.primary">
                    SharePointConfiguration.id
                  </Typography>
                  {': '}
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace' }}
                  >
                    {formData.id}
                  </Typography>
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  Use this numeric id as <code>sharepoint_config_id</code> in ETL PowerShell scripts for the root tenant
                  configuration.
                </Typography>
              </Box>
            )}

            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom>Additional Sites</Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                Each subsite row lists its own database id—use that value as{' '}
                <code>sharepoint_config_id</code> in ETL PowerShell for that site scope.
              </Typography>
              {subSites.length > 0 && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Deleting a site removes it from SecurePoint IQ immediately. Warehouse data for that site
                  is purged in the background and may take several minutes for large libraries.
                </Alert>
              )}
              {subSites.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No subsites have been added yet.
                </Typography>
              )}
              {subSites.map((site) => {
                const siteRowId = site.id ?? site.Id;
                return (
                  <Box
                    key={siteRowId}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: 1,
                      mb: 1.5,
                      py: 0.5
                    }}
                  >
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontFamily: 'ui-monospace, Menlo, Consolas, monospace' }}
                      >
                        SharePointConfiguration.id: {siteRowId}
                      </Typography>
                      <Typography variant="body1">{site.site_path}</Typography>
                    </Box>
                    <Button
                      type="button"
                      size="small"
                      color="error"
                      disabled={deletingSiteId === siteRowId}
                      onClick={() => handleDeleteSubSite(siteRowId)}
                    >
                      {deletingSiteId === siteRowId ? 'Removing…' : 'Delete'}
                    </Button>
                  </Box>
                );
              })}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2, alignItems: 'flex-start' }}>
                <Button
                  type="button"
                  variant="outlined"
                  size="small"
                  onClick={handleDiscoverSites}
                  disabled={isDiscoveringSites || !formData.id}
                >
                  {isDiscoveringSites ? 'Loading sites…' : 'Browse SharePoint sites'}
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2, alignItems: 'flex-start' }}>
                <Autocomplete
                  freeSolo
                  sx={{ flexGrow: 1, minWidth: 280 }}
                  size="small"
                  options={discoverOptions}
                  getOptionLabel={(option) =>
                    typeof option === 'string' ? option : `${option.displayName} (${option.sitePath})`
                  }
                  value={selectedDiscoverSite}
                  inputValue={newSitePath}
                  onChange={(event, newValue) => {
                    if (typeof newValue === 'string') {
                      setNewSitePath(newValue);
                      setSelectedDiscoverSite(null);
                    } else if (newValue) {
                      setSelectedDiscoverSite(newValue);
                      setNewSitePath(newValue.sitePath);
                    } else {
                      setSelectedDiscoverSite(null);
                      setNewSitePath('');
                    }
                    setSitePathTestStatus(null);
                  }}
                  onInputChange={(event, newInputValue, reason) => {
                    if (reason === 'input' || reason === 'clear') {
                      setNewSitePath(newInputValue);
                      setSelectedDiscoverSite(null);
                      setSitePathTestStatus(null);
                    }
                  }}
                  renderOption={(props, option) => (
                    <li {...props} key={option.sitePath}>
                      <Box>
                        <Typography variant="body2">{option.displayName}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.sitePath}
                        </Typography>
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Site path"
                      placeholder="/sites/HR or pick from list"
                      helperText="Browse sites above or type a path like /sites/HR"
                    />
                  )}
                />
                <Button type="button" variant="outlined" size="small" onClick={handleTestSitePath} disabled={isTestingSitePath || !newSitePath.trim()}>
                  {isTestingSitePath ? 'Testing…' : 'Test site'}
                </Button>
                <Button type="button" variant="outlined" size="small" onClick={handleAddSubSite} disabled={!newSitePath.trim()}>
                  Add
                </Button>
              </Box>
              {sitePathTestStatus && (
                <Alert severity={sitePathTestStatus.success ? 'success' : 'error'} sx={{ mt: 1.5 }}>
                  {sitePathTestStatus.message}
                </Alert>
              )}
              {siteActionStatus && (
                <Alert severity={siteActionStatus.success ? 'success' : 'error'} sx={{ mt: 1.5 }}>
                  {siteActionStatus.message}
                </Alert>
              )}
            </Box>

            {testStatus && (
              <Alert 
                severity={testStatus.success ? "success" : "error"}
                sx={{ mt: 2 }}
              >
                {testStatus.message}
              </Alert>
            )}

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                type="button"
                variant="outlined"
                color="primary"
                onClick={handleTestConnection}
                disabled={isTestingConnection || isSaving}
              >
                {isTestingConnection ? 'Testing...' : 'Test Connection'}
              </Button>
              
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isTestingConnection || isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Configuration'}
              </Button>
            </Box>
          </form>
        </Paper>
      </Box>
    </Box>
  );
}

export default SharePointConfig; 